import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Brain,
  CheckCircle2,
  Clock,
  FlaskConical,
  Hand,
  Loader2,
  MessageSquare,
  Pill,
  Plus,
  ShieldCheck,
  Stethoscope,
  Trash2,
  Users,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AiProgress } from "@/components/ui/ai-progress";
import { AnatomyPanel } from "@/features/anatomy/anatomy-panel";
import { casesApi, type EncounterRead } from "@/features/cases/cases.api";
import { ChatPanel } from "@/features/cases/chat-panel";
import { TherapeuticPanel } from "@/features/cases/therapeutic-panel";
import { TurnBubble } from "@/features/cases/turn-bubble";
import { TeamPanel } from "@/features/team/team-panel";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

type Panel =
  | "chat"
  | "exam"
  | "tests"
  | "therapeutic"
  | "team"
  | "anatomy"
  | "differential"
  | "reasoning";

const PANELS: { id: Panel; label: string; icon: typeof MessageSquare; hint: string }[] = [
  { id: "chat", label: "Anamnesis", icon: MessageSquare, hint: "Hablar con el paciente" },
  { id: "exam", label: "Exploración", icon: Hand, hint: "Examen físico por comando (ej. 'ausculto tórax')" },
  { id: "tests", label: "Laboratorio", icon: FlaskConical, hint: "Solicitar exámenes complementarios" },
  { id: "therapeutic", label: "Tratar", icon: Pill, hint: "Decisiones terapéuticas" },
  { id: "team", label: "Equipo", icon: Users, hint: "Interconsulta y pase de visita" },
  { id: "anatomy", label: "Anatomía", icon: ShieldCheck, hint: "Atlas 3D interactivo" },
  { id: "differential", label: "Diferencial", icon: Stethoscope, hint: "Listar diagnósticos" },
  { id: "reasoning", label: "Razonar", icon: Brain, hint: "Articular tu lógica" },
];

export function CasePage() {
  const { encounterId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [panel, setPanel] = useState<Panel>("chat");
  const [closing, setClosing] = useState(false);
  // Keep-alive: cada panel se monta en su 1ª visita y NO se desmonta al cambiar
  // de tab (se oculta con CSS). Así no se pierde lo escrito (diferencial,
  // razonamiento, tratamiento, equipo) al ir y volver entre tabs. (Fix F1.)
  const [visited, setVisited] = useState<Set<Panel>>(() => new Set<Panel>(["chat"]));
  const goToPanel = (p: Panel) => {
    setVisited((s) => (s.has(p) ? s : new Set(s).add(p)));
    setPanel(p);
  };

  const { data: encounter, isLoading, error } = useQuery({
    queryKey: ["encounter", encounterId],
    queryFn: () => casesApi.getEncounter(encounterId),
    enabled: Boolean(encounterId),
  });

  // Autoscroll del timeline al último turno cuando llega una respuesta (U7).
  const turnsEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    turnsEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [encounter?.turns.length]);

  const onTurnsUpdated = (newEncounter: EncounterRead) => {
    queryClient.setQueryData(["encounter", encounterId], newEncounter);
  };

  async function onClose() {
    setClosing(true);
    try {
      const resp = await casesApi.close(encounterId);
      queryClient.setQueryData(["encounter", encounterId], resp.encounter);
      navigate(`/app/cases/${encounterId}/summary`, { state: resp });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Error al cerrar");
      setClosing(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center gap-2 text-[var(--color-text-muted)]">
        <Loader2 className="animate-spin" size={18} /> Cargando caso...
      </div>
    );
  }

  // Antes: `isLoading || !encounter` dejaba la pantalla en "Cargando caso..."
  // para siempre si el fetch fallaba (error → isLoading false pero encounter
  // undefined). Ahora mostramos un estado de error con salida a la biblioteca.
  if (error || !encounter) {
    const msg = error instanceof Error ? error.message : "No se pudo cargar el caso.";
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-4 text-center">
        <div className="text-[15px] font-semibold text-[var(--color-text-primary)]">
          No se pudo cargar el caso
        </div>
        <div className="max-w-md text-[13px] text-[var(--color-text-secondary)]">{msg}</div>
        <button
          type="button"
          onClick={() => navigate("/app/cases")}
          className="rounded-[var(--radius-lg)] border border-[var(--color-border-strong)] px-4 py-2 text-[13px] font-medium text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-surface-2)]"
        >
          ← Volver a la biblioteca
        </button>
      </div>
    );
  }

  const isClosed = encounter.status === "closed";

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/app/cases")}
          className="text-[var(--color-text-secondary)]"
        >
          <ArrowLeft size={14} /> Volver a la biblioteca
        </Button>
        <div className="flex items-center gap-2">
          <Badge variant={isClosed ? "stable" : "info"}>
            {isClosed ? "Caso cerrado" : "Caso activo"}
          </Badge>
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-gold)]/40 bg-[var(--color-gold)]/10 px-3 py-1 font-[var(--font-mono)] text-[12px] font-bold text-[var(--color-gold)]"
            title="Tiempo clínico simulado acumulado"
          >
            <Clock size={13} /> t+{encounter.patient_instance.simulated_time_offset_minutes} min
          </span>
          {!isClosed && (
            <Button variant="gold" size="sm" onClick={onClose} disabled={closing}>
              {closing ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
              Cerrar y evaluar
            </Button>
          )}
        </div>
      </header>

      {closing && (
        <AiProgress estimateSec={20} label="Cerrando el caso y generando la evaluación…" />
      )}

      <div className="grid grid-cols-12 gap-5">
        {/* Timeline izquierda */}
        <section className="col-span-12 lg:col-span-7 rounded-[var(--radius-2xl)] border border-[var(--color-border)] bg-[var(--color-surface)]/70 p-5 backdrop-blur-sm">
          <div className="mb-4 flex items-center gap-2">
            <h3 className="font-[var(--font-display)] text-sm font-semibold uppercase tracking-widest text-[var(--color-text-secondary)]">
              Timeline de la atención
            </h3>
            <span className="ml-auto font-[var(--font-mono)] text-[10px] text-[var(--color-text-muted)]">
              {encounter.turns.length} turnos
            </span>
          </div>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {encounter.turns.map((t) => (
              <TurnBubble key={t.id} turn={t} />
            ))}
            <div ref={turnsEndRef} />
          </div>
        </section>

        {/* Panel lateral */}
        <aside className="col-span-12 lg:col-span-5">
          <div className="sticky top-20 space-y-4">
            <PanelTabs active={panel} onChange={goToPanel} disabled={isClosed} />
            <div className="vision-card-default rounded-[var(--radius-2xl)] p-5">
              {visited.has("chat") && (
                <div className={panel === "chat" ? "" : "hidden"}>
                  <ChatPanel encounterId={encounterId} disabled={isClosed} onUpdate={onTurnsUpdated} />
                </div>
              )}
              {visited.has("exam") && (
                <div className={panel === "exam" ? "" : "hidden"}>
                  <ExamPanel encounterId={encounterId} disabled={isClosed} onUpdate={onTurnsUpdated} />
                </div>
              )}
              {visited.has("tests") && (
                <div className={panel === "tests" ? "" : "hidden"}>
                  <TestsPanel encounterId={encounterId} disabled={isClosed} onUpdate={onTurnsUpdated} />
                </div>
              )}
              {visited.has("therapeutic") && (
                <div className={panel === "therapeutic" ? "" : "hidden"}>
                  <TherapeuticPanel encounterId={encounterId} disabled={isClosed} onUpdate={onTurnsUpdated} />
                </div>
              )}
              {visited.has("team") && (
                <div className={panel === "team" ? "" : "hidden"}>
                  <TeamPanel encounterId={encounterId} disabled={isClosed} />
                </div>
              )}
              {visited.has("anatomy") && (
                <div className={panel === "anatomy" ? "" : "hidden"}>
                  <AnatomyPanel compact />
                </div>
              )}
              {visited.has("differential") && (
                <div className={panel === "differential" ? "" : "hidden"}>
                  <DifferentialPanel encounterId={encounterId} disabled={isClosed} />
                </div>
              )}
              {visited.has("reasoning") && (
                <div className={panel === "reasoning" ? "" : "hidden"}>
                  <ReasoningPanel encounterId={encounterId} disabled={isClosed} />
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function PanelTabs({
  active,
  onChange,
  disabled,
}: {
  active: Panel;
  onChange: (p: Panel) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-1.5 rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-surface)]/60 p-1.5">
      {PANELS.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          disabled={disabled}
          className={cn(
            "flex flex-1 items-center justify-center gap-1.5 rounded-[var(--radius-md)] px-2.5 py-2 text-[11px] font-semibold uppercase tracking-widest transition-all",
            active === id
              ? "bg-[var(--color-blue)] text-white shadow-[var(--shadow-glow-blue)]"
              : "text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)]",
            disabled && "opacity-50 cursor-not-allowed",
          )}
        >
          <Icon size={13} />
          <span className="hidden sm:inline">{label}</span>
        </button>
      ))}
    </div>
  );
}

// ---------- EXAM ----------
function ExamPanel({
  encounterId,
  disabled,
  onUpdate,
}: {
  encounterId: string;
  disabled: boolean;
  onUpdate: (e: EncounterRead) => void;
}) {
  const [cmd, setCmd] = useState("");
  const suggestions = [
    "ausculto corazón",
    "evalúo yugulares a 45°",
    "ausculto pulmones",
    "mido PA en ambos brazos",
    "palpo abdomen",
    "evalúo Glasgow",
    "pulsos periféricos",
    "signo de Homans en ambas pantorrillas",
  ];
  const mut = useMutation({
    mutationFn: (c: string) => casesApi.physicalExam(encounterId, c),
    onSuccess: (e) => {
      onUpdate(e);
      setCmd("");
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Error al examinar"),
  });

  return (
    <div className="space-y-3">
      <PanelHeader
        icon={<Hand size={14} />}
        title="Examen físico por comando"
        hint="El parser mapea lo que escribes al hallazgo específico."
      />
      <Input
        value={cmd}
        onChange={(e) => setCmd(e.target.value)}
        disabled={disabled || mut.isPending}
        placeholder="Ej: ausculto foco mitral"
      />
      <div className="flex flex-wrap gap-1.5">
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setCmd(s)}
            disabled={disabled || mut.isPending}
            className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-[11px] text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-blue)]/40 hover:text-[var(--color-text-primary)] disabled:opacity-50"
          >
            {s}
          </button>
        ))}
      </div>
      <Button
        onClick={() => mut.mutate(cmd)}
        disabled={disabled || mut.isPending || cmd.trim().length === 0}
        className="w-full"
      >
        {mut.isPending ? <Loader2 className="animate-spin" size={14} /> : <Hand size={14} />}
        Ejecutar examen
      </Button>
    </div>
  );
}

// ---------- TESTS ----------
function TestsPanel({
  encounterId,
  disabled,
  onUpdate,
}: {
  encounterId: string;
  disabled: boolean;
  onUpdate: (e: EncounterRead) => void;
}) {
  const { data: tests } = useQuery({
    queryKey: ["tests-available", encounterId],
    queryFn: () => casesApi.testsAvailable(encounterId),
  });
  const mut = useMutation({
    mutationFn: (key: string) => casesApi.orderTest(encounterId, key),
    onSuccess: (e) => onUpdate(e),
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Error al solicitar el examen"),
  });

  return (
    <div className="space-y-3">
      <PanelHeader
        icon={<FlaskConical size={14} />}
        title="Exámenes complementarios"
        hint="El timing simulado avanza al solicitar cada examen."
      />
      <div className="grid grid-cols-1 gap-2">
        {(tests ?? []).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => mut.mutate(t)}
            disabled={disabled || mut.isPending}
            className="group flex items-center justify-between rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)]/80 px-3 py-2.5 text-left text-[13px] text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-blue)]/40 hover:bg-[var(--color-surface-2)] disabled:opacity-50"
          >
            <span className="font-[var(--font-mono)] text-[12px]">
              {t.replace(/_/g, " ")}
            </span>
            <Plus
              size={14}
              className="text-[var(--color-text-muted)] group-hover:text-[var(--color-blue-soft)]"
            />
          </button>
        ))}
        {(!tests || tests.length === 0) && (
          <div className="text-[12px] text-[var(--color-text-muted)]">
            No hay exámenes configurados en esta receta.
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- DIFFERENTIAL ----------
interface DxItem {
  diagnostico: string;
  probabilidad: string;
  justificacion: string;
}

function DifferentialPanel({
  encounterId,
  disabled,
}: {
  encounterId: string;
  disabled: boolean;
}) {
  const [items, setItems] = useState<DxItem[]>([
    { diagnostico: "", probabilidad: "alta", justificacion: "" },
  ]);
  const [reasoning, setReasoning] = useState("");
  const [evaluation, setEvaluation] = useState<Record<string, unknown> | null>(null);
  const mut = useMutation({
    mutationFn: () =>
      casesApi.submitDifferential(
        encounterId,
        items
          .filter((i) => i.diagnostico.trim().length > 0)
          .map((i) => ({
            diagnostico: i.diagnostico,
            probabilidad: i.probabilidad,
            justificacion: i.justificacion,
          })),
        reasoning || undefined,
      ),
    onSuccess: (d) => {
      setEvaluation(d.evaluation);
      toast.success("Diferencial evaluado");
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Error al evaluar"),
  });

  const updateItem = (idx: number, patch: Partial<DxItem>) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () =>
    setItems((prev) => [...prev, { diagnostico: "", probabilidad: "media", justificacion: "" }]);
  const removeItem = (idx: number) =>
    setItems((prev) => prev.filter((_, i) => i !== idx));

  const score = (evaluation?.score_global as number | undefined) ?? null;

  return (
    <div className="space-y-3">
      <PanelHeader
        icon={<Stethoscope size={14} />}
        title="Diagnóstico diferencial"
        hint="Ordena por probabilidad. La evaluación compara contra la receta."
      />
      {items.map((it, idx) => (
        <div
          key={idx}
          className="space-y-2 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)]/60 p-3"
        >
          <div className="flex items-center gap-2">
            <span className="font-[var(--font-mono)] text-[11px] text-[var(--color-text-muted)]">
              #{idx + 1}
            </span>
            <Input
              value={it.diagnostico}
              onChange={(e) => updateItem(idx, { diagnostico: e.target.value })}
              disabled={disabled || mut.isPending}
              placeholder="Diagnóstico (ej: SCA con elevación ST)"
              className="h-8 text-[13px]"
            />
            <select
              value={it.probabilidad}
              onChange={(e) => updateItem(idx, { probabilidad: e.target.value })}
              disabled={disabled || mut.isPending}
              className="h-8 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-[12px] text-[var(--color-text-primary)]"
            >
              <option value="muy alta">muy alta</option>
              <option value="alta">alta</option>
              <option value="media">media</option>
              <option value="baja">baja</option>
            </select>
            {items.length > 1 && (
              <button
                type="button"
                onClick={() => removeItem(idx)}
                className="text-[var(--color-text-muted)] hover:text-[var(--color-critical)]"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
          <Textarea
            rows={2}
            value={it.justificacion}
            onChange={(e) => updateItem(idx, { justificacion: e.target.value })}
            disabled={disabled || mut.isPending}
            placeholder="Justificación clínica"
          />
        </div>
      ))}

      <Button
        variant="ghost"
        size="sm"
        onClick={addItem}
        disabled={disabled || mut.isPending || items.length >= 6}
      >
        <Plus size={14} /> Agregar diferencial
      </Button>

      <Textarea
        rows={3}
        value={reasoning}
        onChange={(e) => setReasoning(e.target.value)}
        disabled={disabled || mut.isPending}
        placeholder="Razonamiento global (opcional)"
      />

      <Button
        onClick={() => mut.mutate()}
        disabled={
          disabled || mut.isPending || items.every((i) => i.diagnostico.trim() === "")
        }
        className="w-full"
      >
        {mut.isPending ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
        Enviar y evaluar
      </Button>

      {evaluation && <EvaluationCard evaluation={evaluation} score={score} />}
    </div>
  );
}

function EvaluationCard({
  evaluation,
  score,
}: {
  evaluation: Record<string, unknown>;
  score: number | null;
}) {
  const gaps = (evaluation.gaps as Array<Record<string, unknown>> | undefined) ?? [];
  const feedback = (evaluation.feedback_alumno as string | undefined) ?? "";
  return (
    <div className="mt-3 space-y-3 rounded-[var(--radius-lg)] border border-[var(--color-gold)]/25 bg-[var(--color-gold)]/5 p-4">
      <div className="flex items-center gap-3">
        <Badge variant="gold">Evaluación</Badge>
        {score !== null && (
          <span className="font-[var(--font-mono)] text-2xl font-extrabold text-[var(--color-gold)]">
            {score}/100
          </span>
        )}
      </div>
      {gaps.length > 0 && (
        <ul className="space-y-1.5">
          {gaps.map((g, i) => (
            <li key={i} className="flex gap-2 text-[12px]">
              <XCircle size={14} className="mt-0.5 shrink-0 text-[var(--color-critical)]" />
              <span className="text-[var(--color-text-secondary)]">
                {(g.descripcion as string) ?? JSON.stringify(g)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {feedback && (
        <div className="whitespace-pre-wrap text-[13px] text-[var(--color-text-primary)]">
          {feedback}
        </div>
      )}
    </div>
  );
}

// ---------- REASONING ----------
function ReasoningPanel({
  encounterId,
  disabled,
}: {
  encounterId: string;
  disabled: boolean;
}) {
  const [text, setText] = useState("");
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const mut = useMutation({
    mutationFn: () => casesApi.submitReasoning(encounterId, text),
    onSuccess: (sr) => {
      setResult(sr.comparison_result);
      toast.success("Razonamiento comparado con la traza experta");
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Error al comparar"),
  });

  return (
    <div className="space-y-3">
      <PanelHeader
        icon={<Brain size={14} />}
        title="Articular razonamiento"
        hint="Cuenta tu lógica clínica. El experto genera una traza paralela y la compara."
      />
      <Textarea
        rows={8}
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={disabled || mut.isPending}
        placeholder="Ej: Mi hipótesis principal es X porque... Mi diferencial incluye Y y Z. Los red flags que identifiqué son..."
      />
      <Button
        onClick={() => mut.mutate()}
        disabled={disabled || mut.isPending || text.trim().length < 10}
        className="w-full"
      >
        {mut.isPending ? <Loader2 className="animate-spin" size={14} /> : <Brain size={14} />}
        Comparar con el experto
      </Button>
      {mut.isPending && (
        <AiProgress estimateSec={40} label="Generando la traza experta y comparando…" />
      )}
      {result && <ReasoningResultCard result={result} />}
    </div>
  );
}

function ReasoningResultCard({ result }: { result: Record<string, unknown> }) {
  const alineamiento = result.alineamiento_global as
    | { nivel?: string; puntos_acuerdo?: string[]; puntos_divergencia?: string[] }
    | undefined;
  const divergencias = (result.divergencias as Array<Record<string, unknown>> | undefined) ?? [];
  const sesgos = (result.sesgos_detectados as Array<Record<string, unknown>> | undefined) ?? [];
  const preguntas = (result.preguntas_socraticas as string[] | undefined) ?? [];
  const recomendacion = result.recomendacion_estilo_aprendizaje as string | undefined;

  const nivelVariant =
    alineamiento?.nivel === "alto"
      ? "stable"
      : alineamiento?.nivel === "medio"
        ? "urgent"
        : "critical";

  return (
    <div className="mt-3 space-y-4 rounded-[var(--radius-lg)] border border-[var(--color-blue)]/25 bg-[var(--color-blue)]/5 p-4">
      <div className="flex items-center gap-2">
        <Badge variant="info">Traza comparada</Badge>
        {alineamiento?.nivel && (
          <Badge variant={nivelVariant} size="sm">
            Alineamiento: {alineamiento.nivel}
          </Badge>
        )}
      </div>
      {divergencias.length > 0 && (
        <Section title={`Divergencias (${divergencias.length})`}>
          {divergencias.map((d, i) => (
            <li key={i} className="flex gap-2">
              <XCircle size={12} className="mt-1 shrink-0 text-[var(--color-urgent)]" />
              <div>
                <div className="font-semibold text-[var(--color-text-primary)]">
                  {(d.tipo as string) ?? "divergencia"}
                </div>
                <div className="text-[12px] text-[var(--color-text-secondary)]">
                  {(d.descripcion as string) ?? ""}
                </div>
              </div>
            </li>
          ))}
        </Section>
      )}
      {sesgos.length > 0 && (
        <Section title="Sesgos detectados">
          {sesgos.map((s, i) => (
            <li key={i} className="text-[12px] text-[var(--color-text-secondary)]">
              <span className="font-mono text-[11px] text-[var(--color-gold)]">
                {(s.sesgo as string) ?? "sesgo"}
              </span>
              {" — "}
              {(s.evidencia as string) ?? ""}
            </li>
          ))}
        </Section>
      )}
      {preguntas.length > 0 && (
        <Section title="Preguntas socráticas">
          {preguntas.map((p, i) => (
            <li key={i} className="text-[13px] text-[var(--color-text-primary)]">
              {i + 1}. {p}
            </li>
          ))}
        </Section>
      )}
      {recomendacion && (
        <div className="rounded-[var(--radius-md)] border border-[var(--color-gold)]/30 bg-[var(--color-gold)]/5 p-3 text-[12.5px] text-[var(--color-text-primary)]">
          <span className="mr-1 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-gold)]">
            Recomendación
          </span>
          {recomendacion}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-2 font-[var(--font-mono)] text-[10px] font-semibold uppercase tracking-widest text-[var(--color-text-muted)]">
        {title}
      </div>
      <ul className="space-y-1.5">{children}</ul>
    </div>
  );
}

function PanelHeader({
  icon,
  title,
  hint,
}: {
  icon: React.ReactNode;
  title: string;
  hint: string;
}) {
  return (
    <header className="space-y-1">
      <div className="flex items-center gap-2 font-[var(--font-display)] text-sm font-bold text-[var(--color-text-primary)]">
        <span className="text-[var(--color-blue-soft)]">{icon}</span>
        {title}
      </div>
      <div className="text-[11.5px] text-[var(--color-text-muted)]">{hint}</div>
    </header>
  );
}

